import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import LanguageSelector from '../components/LanguageSelector';
import ProductCard from '../components/ProductCard';
import { useTranslation } from '../hooks/useTranslation';
import { prodottiAPI } from '../config/api';
import './Prodotto.css';

const Prodotto = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { t } = useTranslation();
  
  const [prodotto, setProdotto] = useState(null);
  const [quantita, setQuantita] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [prodottiSimili, setProdottiSimili] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);

  useEffect(() => {
    loadProdotto();
  }, [id]);

  const loadProdotto = async () => {
    try {
      setLoading(true);
      const data = await prodottiAPI.getById(id);
      
      // Recupera le immagini del prodotto
      if (data && data.foto) {
        try {
          const API_BASE_URL = process.env.NODE_ENV === 'production' 
            ? '/shop/backend/api' 
            : 'http://efwypvu.cluster021.hosting.ovh.net/shop/backend/api';
          
          const imgResponse = await fetch(`${API_BASE_URL}/get_images.php?folder=${data.foto}`);
          const imgData = await imgResponse.json();
          
          if (imgData.success) {
            // Aggiungi le immagini al prodotto
            data.mainImageUrl = imgData.data.main_image?.full_url || null;
            data.images = imgData.data.all_images?.map(img => img.full_url) || [];
            data.additionalImages = imgData.data.additional_images?.map(img => img.full_url) || [];
          }
        } catch (e) {
          console.warn('Errore recupero immagini prodotto:', e);
        }
      }
      
      // Debug: verifica che quantita sia presente
      console.log('📦 Prodotto caricato:', {
        id: data?.id_prodotto,
        titolo: data?.titolo,
        quantita: data?.quantita,
        quantitaType: typeof data?.quantita
      });
      
      setProdotto(data);
      
      // Carica prodotti simili della stessa categoria
      if (data && data.id_categoria) {
        const prodottiSimili = await prodottiAPI.getByCategoria(data.id_categoria);
        
        // Recupera le immagini per i prodotti simili
        const prodottiSimiliConImmagini = await Promise.all(
          prodottiSimili.map(async (prodottoSimile) => {
            if (prodottoSimile.foto) {
              try {
                const API_BASE_URL = process.env.NODE_ENV === 'production' 
                  ? '/shop/backend/api' 
                  : 'http://efwypvu.cluster021.hosting.ovh.net/shop/backend/api';
                
                const imgResponse = await fetch(`${API_BASE_URL}/get_images.php?folder=${prodottoSimile.foto}`);
                const imgData = await imgResponse.json();
                
                if (imgData.success && imgData.data.main_image) {
                  prodottoSimile.mainImageUrl = imgData.data.main_image.full_url;
                }
              } catch (e) {
                console.warn('Errore recupero immagine prodotto simile:', e);
              }
            }
            return prodottoSimile;
          })
        );
        
        // Escludi il prodotto corrente
        const filtered = prodottiSimiliConImmagini.filter(p => p.id_prodotto !== data.id_prodotto).slice(0, 4);
        setProdottiSimili(filtered);
      }
      
      setError(null);
    } catch (err) {
      setError('Prodotto non trovato.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Normalizza la quantità: può arrivare come numero, stringa o undefined
  const availableStock = prodotto 
    ? (typeof prodotto.quantita === 'number' 
        ? prodotto.quantita 
        : (typeof prodotto.quantita === 'string' 
            ? parseInt(prodotto.quantita, 10) || 0 
            : 0))
    : 0;
  const isOutOfStock = availableStock <= 0;

  useEffect(() => {
    if (prodotto) {
      setQuantita(availableStock > 0 ? 1 : 0);
    }
  }, [prodotto, availableStock]);

  const handleAddToCart = () => {
    if (isOutOfStock) return;
    addToCart(prodotto, quantita);
    setAddedToCart(true);
    
    setTimeout(() => {
      setAddedToCart(false);
    }, 2000);
  };

  const handleBuyNow = () => {
    if (isOutOfStock) return;
    addToCart(prodotto, quantita);
    navigate('/carrello');
  };

  // Funzioni per gestire le immagini
  const getProductImages = () => {
    if (!prodotto) return [];
    const images = [];
    
    // Aggiungi immagine principale se esiste
    if (prodotto.mainImageUrl) {
      images.push(prodotto.mainImageUrl);
    }
    
    // Aggiungi altre immagini se esistono (escludendo quella principale se già presente)
    if (prodotto.additionalImages && prodotto.additionalImages.length > 0) {
      images.push(...prodotto.additionalImages);
    }
    
    // Se non ci sono immagini, usa placeholder
    if (images.length === 0) {
      images.push('https://via.placeholder.com/600x600/2C5F2D/FFFFFF?text=Prodotto');
    }
    
    return images;
  };

  const nextImage = () => {
    const images = getProductImages();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    const images = getProductImages();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const selectImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Funzioni per il modal delle immagini
  const openImageModal = () => {
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
  };

  const nextImageModal = () => {
    const images = getProductImages();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImageModal = () => {
    const images = getProductImages();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const selectImageModal = (index) => {
    setCurrentImageIndex(index);
  };

  // Gestione tasti per il modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showImageModal) {
        if (e.key === 'Escape') {
          closeImageModal();
        } else if (e.key === 'ArrowLeft') {
          prevImageModal();
        } else if (e.key === 'ArrowRight') {
          nextImageModal();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showImageModal]);

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Caricamento prodotto...</p>
        </div>
      </div>
    );
  }

  if (error || !prodotto) {
    return (
      <div className="container">
        <div className="error">
          <h2>{error || 'Prodotto non trovato'}</h2>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            {t('product.back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="prodotto-page">
      <div className="container">
        <button className="btn-back" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left"></i> {t('product.back')}
        </button>

        <div className="prodotto-detail">
          <div className="prodotto-image-section">
            <div className="prodotto-image-main">
              <button className="image-nav-btn prev-btn" onClick={prevImage}>
                <i className="fas fa-chevron-left"></i>
              </button>
              <img 
                src={getProductImages()[currentImageIndex]} 
                alt={prodotto.titolo}
                onClick={openImageModal}
                className="main-product-image"
              />
              <button className="image-nav-btn next-btn" onClick={nextImage}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
            
            {getProductImages().length > 1 && (
              <div className="prodotto-image-thumbnails">
                {getProductImages().map((image, index) => (
                  <button
                    key={index}
                    className={`thumbnail-btn ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => selectImage(index)}
                  >
                    <img src={image} alt={`${prodotto.titolo} ${index + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="prodotto-info-large">
            <h1>{prodotto.titolo}</h1>
            
            {prodotto.categoria_nome && (
              <p className="prodotto-category-badge">{prodotto.categoria_nome}</p>
            )}

            <div className="prodotto-price-large">
              € {parseFloat(prodotto.prezzo).toFixed(2)}
            </div>

            {prodotto.descrizione && (
              <div className="prodotto-description">
                <p>{prodotto.descrizione}</p>
              </div>
            )}

            <div className="prodotto-actions">
              <div className="quantity-selector">
                <label>{t('product.quantity')}:</label>
                <div className="quantity-controls">
                  <button 
                    onClick={() => setQuantita(Math.max(1, quantita - 1))}
                    className="quantity-btn"
                    disabled={isOutOfStock || quantita <= 1}
                  >
                    <i className="fas fa-minus"></i>
                  </button>
                  <input 
                    type="number" 
                    value={quantita} 
                    onChange={(e) => {
                      const value = Math.max(1, parseInt(e.target.value, 10) || 1);
                      setQuantita(Math.min(value, Math.max(1, availableStock)));
                    }}
                    min="1"
                    className="quantity-input"
                    disabled={isOutOfStock}
                  />
                  <button 
                    onClick={() => setQuantita(Math.min(quantita + 1, availableStock))}
                    className="quantity-btn"
                    disabled={isOutOfStock || quantita >= availableStock}
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                </div>
              </div>

              <p className={`stock-status ${isOutOfStock ? 'out' : 'in'}`}>
                {isOutOfStock
                  ? t('product.out_of_stock')
                  : t('product.stock_available', { values: { quantity: availableStock } })}
              </p>

              <div className="action-buttons">
                <button 
                  className="btn btn-primary btn-large"
                  onClick={handleAddToCart}
                  disabled={isOutOfStock}
                >
                  <i className="fas fa-shopping-cart"></i> {addedToCart ? 'Aggiunto!' : t('product.add_to_cart')}
                </button>
                <button 
                  className="btn btn-secondary btn-large"
                  onClick={handleBuyNow}
                  disabled={isOutOfStock}
                >
                  {t('product.buy_now')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sezione Prodotti Simili */}
        {prodottiSimili.length > 0 && (
          <div className="prodotti-simili-section">
            <h2>{t('product.similar_products')}</h2>
            <div className="prodotti-simili-grid">
              {prodottiSimili.map((prodottoSimile) => {
                // Normalizza i dati per ProductCard (stessa struttura della home)
                const prodottoNormalizzato = {
                  id: prodottoSimile.id_prodotto,
                  nome: prodottoSimile.titolo,
                  prezzo: prodottoSimile.prezzo,
                  foto: prodottoSimile.mainImageUrl || null,
                  quantita: prodottoSimile.quantita ?? 0,
                  categorie: prodottoSimile.categoria_nome || undefined,
                  id_categoria: prodottoSimile.id_categoria
                };
                
                return (
                  <ProductCard 
                    key={prodottoSimile.id_prodotto} 
                    prodotto={prodottoNormalizzato} 
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Modal Immagine a Tutto Schermo */}
        {showImageModal && (
          <div className="image-modal-overlay" onClick={closeImageModal}>
            <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="image-modal-close" onClick={closeImageModal}>
                <i className="fas fa-times"></i>
              </button>
              
              <button className="image-modal-nav prev" onClick={prevImageModal}>
                <i className="fas fa-chevron-left"></i>
              </button>
              
              <div className="image-modal-main">
                <img 
                  src={getProductImages()[currentImageIndex]} 
                  alt={`${prodotto.titolo} - Immagine ${currentImageIndex + 1}`}
                  className="modal-image"
                />
              </div>
              
              <button className="image-modal-nav next" onClick={nextImageModal}>
                <i className="fas fa-chevron-right"></i>
              </button>
              
              {getProductImages().length > 1 && (
                <div className="image-modal-thumbnails">
                  {getProductImages().map((image, index) => (
                    <button
                      key={index}
                      className={`modal-thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => selectImageModal(index)}
                    >
                      <img src={image} alt={`${prodotto.titolo} ${index + 1}`} />
                    </button>
                  ))}
                </div>
              )}
              
              <div className="image-modal-info">
                <span className="image-counter">
                  {currentImageIndex + 1} / {getProductImages().length}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Language Selector */}
      <LanguageSelector />
    </div>
  );
};

export default Prodotto;

